/**
 * Query参数统一管理类
 * 支持从URL参数同步
 * 
 * @example
 * // ========== 基础用法 ==========
 * // 1. 在组件constructor中初始化
 * constructor(props) {
 *   super(props);
 *   const defaultQuery = { date: '2024-01-01', store_id: 59 };
 *   
 *   this.queryManager = new QueryManager(defaultQuery, {
 *     syncToUrl: true // 需要同步到URL时，显式设置为true
 *   });
 *   
 *   // 设置更新回调（自动触发接口调用）
 *   this.queryManager.setUpdateCallback((query) => {
 *     this.fetchData(query);
 *   });
 *   
 *   // 设置navigate函数（用于URL同步）
 *   if (props.navigate) {
 *     this.queryManager.setNavigate(props.navigate);
 *   }
 * }
 * 
 * // 2. 在componentDidMount中初始化query
 * componentDidMount() {
 *   const { location } = this.props;
 *   // 从 URL 和 defaultQuery 初始化
 *   const initializedQuery = this.queryManager.initialize(location);
 *   
 *   // 使用初始化后的query加载数据
 *   this.fetchData(initializedQuery);
 * }
 * 
 * // ========== 高级用法 ==========
 * // 自定义URL参数解析
 * this.queryManager = new QueryManager(defaultQuery, {
 *   parseUrlValue: (value, key) => {
 *     // 自定义解析逻辑
 *     if (key === 'date') {
 *       return dayjs(value).format('YYYY-MM-DD');
 *     }
 *     return value;
 *   }
 * });
 * 
 * // ========== 使用示例 ==========
 * // 单个更新（自动触发接口调用）
 * this.queryManager.updateQuery('store_id', 60);
 * 
 * // 批量更新（自动触发接口调用）
 * this.queryManager.updateQuery({ store_id: 60, date: '2024-01-02' });
 * 
 * // 更新但不自动调用接口
 * this.queryManager.updateQuery('store_id', 60, { autoFetch: false });
 * 
 * // 更新但不同步到URL
 * this.queryManager.updateQuery('store_id', 60, { syncToUrl: false });
 * 
 * // 获取当前query
 * const currentQuery = this.queryManager.getQuery();
 * 
 * // 重置query为默认值
 * this.queryManager.resetQuery();
 * 
 * // 重置query但不同步到URL
 * this.queryManager.resetQuery({ syncToUrl: false });
 * 
 * // 设置完整query（替换整个对象）
 * this.queryManager.setQuery({ store_id: 60, date: '2024-01-01' });
 * 
 * // 设置query但不同步到URL
 * this.queryManager.setQuery({ store_id: 60, date: '2024-01-01' }, { syncToUrl: false });
 */
class QueryManager {
  /**
   * @param {Object} defaultQuery - 默认查询参数
   * @param {Object} options - 配置选项
   * @param {boolean} options.syncToUrl - 是否同步到URL，默认false（需要显式设置为true）
   * @param {Function} options.parseUrlValue - 自定义URL参数值解析函数
   */
  constructor(defaultQuery = {}, options = {}) {
    this.defaultQuery = { ...defaultQuery };
    this.currentQuery = { ...defaultQuery };
    this.onUpdateCallback = null; // query更新回调函数
    this.syncToUrl = options.syncToUrl === true; // 默认不同步到URL，需要显式设置为true
    this.parseUrlValue = options.parseUrlValue || null; // 自定义URL值解析函数
    this.navigate = null; // navigate函数（react-router）
  }

  /**
   * 设置更新回调函数（用于自动触发接口调用）
   * @param {Function} callback - 回调函数，接收更新后的query作为参数
   */
  setUpdateCallback(callback) {
    this.onUpdateCallback = callback;
  }

  /**
   * 从URL参数同步query
   * @param {Object} location - react-router的location对象
   * @returns {Object} URL中的query参数
   */
  syncFromURL(location) {
    if (!location) return {};
    
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = {};
    
    searchParams.forEach((value, key) => {
      if (this.parseUrlValue) {
        // 使用自定义解析函数
        urlQuery[key] = this.parseUrlValue(value, key);
      } else {
        // 默认解析逻辑：尝试解析为数字
        if (!isNaN(value) && value !== '') {
          urlQuery[key] = Number(value);
        } else {
          urlQuery[key] = value;
        }
      }
    });
    
    return urlQuery;
  }

  /**
   * 初始化query（优先级：URL > defaultQuery）
   * @param {Object} location - react-router的location对象（可选）
   * @returns {Object} 初始化后的query对象
   */
  initialize(location = null) {
    const urlQuery = this.syncFromURL(location);
    
    // 合并优先级：URL > defaultQuery
    this.currentQuery = {
      ...this.defaultQuery,
      ...urlQuery
    };
    
    return this.currentQuery;
  }

  /**
   * 获取当前query
   * @returns {Object} 当前query对象的副本
   */
  getQuery() {
    return { ...this.currentQuery };
  }

  /**
   * 更新query（支持单个key-value或批量更新）
   * @param {string|Object} keyOrUpdates - 单个key或更新对象
   * @param {*} value - 当第一个参数是key时的value值
   * @param {Object} options - 选项
   * @param {boolean} options.autoFetch - 是否自动触发接口调用，默认true
   * @param {boolean} options.syncToUrl - 是否同步到URL，默认使用构造函数配置
   * @returns {Object} 更新后的query对象
   * 
   * @example
   * // 单个更新
   * queryManager.updateQuery('store_id', 60);
   * 
   * // 批量更新
   * queryManager.updateQuery({ store_id: 60, date: '2024-01-01' });
   * 
   * // 更新但不自动调用接口
   * queryManager.updateQuery('store_id', 60, { autoFetch: false });
   */
  updateQuery(keyOrUpdates, value, options = {}) {
    const {
      autoFetch = true, // 是否自动触发接口调用
      syncToUrl = this.syncToUrl
    } = options;

    // 构建更新对象
    let updates = {};
    if (typeof keyOrUpdates === 'string') {
      updates[keyOrUpdates] = value;
    } else if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      updates = keyOrUpdates;
    } else {
      console.warn('[QueryManager] updateQuery参数格式错误');
      return this.currentQuery;
    }

    // 更新query
    this.currentQuery = {
      ...this.currentQuery,
      ...updates
    };

    // 同步到URL（需要navigate函数，在组件中处理）
    if (syncToUrl && this.navigate) {
      const searchParams = new URLSearchParams();
      Object.keys(this.currentQuery).forEach(key => {
        const val = this.currentQuery[key];
        if (val !== undefined && val !== null && val !== '') {
          searchParams.set(key, String(val));
        }
      });
      this.navigate(`?${searchParams.toString()}`, { replace: true });
    }

    // 自动触发接口调用
    if (autoFetch && this.onUpdateCallback) {
      this.onUpdateCallback(this.currentQuery);
    }

    return this.currentQuery;
  }

  /**
   * 重置query为默认值
   * @param {Object} options - 选项
   * @param {boolean} options.syncToUrl - 是否同步到URL，默认使用构造函数配置
   * @returns {Object} 重置后的query对象
   * 
   * @example
   * // 重置query，不同步到URL
   * queryManager.resetQuery({ syncToUrl: false });
   */
  resetQuery(options = {}) {
    const {
      syncToUrl = this.syncToUrl
    } = options;

    this.currentQuery = { ...this.defaultQuery };

    // 清除URL参数
    if (syncToUrl && this.navigate) {
      this.navigate('?', { replace: true });
    }

    // 触发接口调用
    if (this.onUpdateCallback) {
      this.onUpdateCallback(this.currentQuery);
    }

    return this.currentQuery;
  }

  /**
   * 设置完整的query（替换整个query对象）
   * @param {Object} newQuery - 新的query对象
   * @param {Object} options - 选项
   * @param {boolean} options.syncToUrl - 是否同步到URL，默认使用构造函数配置
   * @returns {Object} 设置后的query对象
   * 
   * @example
   * // 设置query，不同步到URL
   * queryManager.setQuery({ store_id: 60, date: '2024-01-01' }, { syncToUrl: false });
   */
  setQuery(newQuery, options = {}) {
    const {
      syncToUrl = this.syncToUrl
    } = options;

    this.currentQuery = { ...newQuery };

    // 同步到URL
    if (syncToUrl && this.navigate) {
      const searchParams = new URLSearchParams();
      Object.keys(this.currentQuery).forEach(key => {
        const val = this.currentQuery[key];
        if (val !== undefined && val !== null && val !== '') {
          searchParams.set(key, String(val));
        }
      });
      this.navigate(`?${searchParams.toString()}`, { replace: true });
    }

    // 触发接口调用
    if (this.onUpdateCallback) {
      this.onUpdateCallback(this.currentQuery);
    }

    return this.currentQuery;
  }

  /**
   * 设置navigate函数（用于URL同步）
   * @param {Function} navigate - react-router的navigate函数
   */
  setNavigate(navigate) {
    this.navigate = navigate;
  }
}

export default QueryManager;

